'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { use, useEffect, useState } from 'react';
import { useMediaQuery } from 'usehooks-ts';
import SearchToolbar from '@/app/[locale]/(worlds)/(worlds-landing)/search/components/SearchToolbar';
import ExploreViverseAllSections from '@/app/[locale]/(worlds)/components/ExploreViverseAllSections';
import ExploreViverseCardGrid from '@/app/[locale]/(worlds)/components/ExploreViverseCardGrid';
import ExploreViverseTags, { ExploreViverseTagsSkeleton } from '@/app/[locale]/(worlds)/components/ExploreViverseTags';
import { EXPLORE_VIVERSE_LIMIT } from '@/app/[locale]/(worlds)/components/useExploreViverseData';
import { ViverseWorldCardSkeleton } from '@/components/ViverseWorldCardSkeleton';
import IconExperience from '@/components/assets/icon_experimence.svg';
import IconGamepad from '@/components/assets/icon_gamepad.svg';
import { Separator } from '@/components/ui/separator';
import { CARD_GRID_AUTO } from '@/constants/card';
import { SEARCH_PARAM_KEYS } from '@/constants/search';
import { useLayoutContext } from '@/context/LayoutContext';
import { useWorldSearchContext } from '@/context/WorldSearchContext';
import useHash from '@/hooks/useHash';
import { useFallbackSkeletons } from '@/hooks/useScrollRestoration';
import { useSearchParamsSchema } from '@/hooks/useSearchParamsSchema';
import { replaceUrlWithQueryString } from '@/lib/search';
import { RoomTag } from '@/types/genre';
import { WorldTypeValue } from '@/types/search';
import { ViverseWorldCardType } from '@/types/viverse-world-card';
type ExploreViverseProps = {
  promotedRoomTagsPromise: Promise<RoomTag[]>;
};

// 🥷🏼 workaround for "all-experiences" and string for intelligence restriction
type TagName = 'all-experiences' | (string & {});
const EXPLORE_CONTENT_TYPES = ['game', 'experience'] as const;
type ExploreContentType = (typeof EXPLORE_CONTENT_TYPES)[number];
type ContentTypeTagKey = 'all' | ExploreContentType;
const isExploreContentType = (type: WorldTypeValue | null): type is ExploreContentType => type !== null && EXPLORE_CONTENT_TYPES.includes(type as ExploreContentType);
const EXPLORE_TYPE_TO_CARD_TYPE: Record<ExploreContentType, ViverseWorldCardType> = {
  game: 'games',
  experience: 'experiences'
};
export default function ExploreViverse(t0) {
  const $ = _c(73);
  const {
    promotedRoomTagsPromise
  } = t0;
  const promotedRoomTags = use(promotedRoomTagsPromise);
  const {
    logoHeight
  } = useLayoutContext();
  const {
    mobileNavHeight
  } = useWorldSearchContext();
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const hash = useHash();
  const {
    genre,
    type
  } = useSearchParamsSchema();
  const urlTag = genre || "all-experiences";
  let t1;
  if ($[0] !== type) {
    t1 = isExploreContentType(type) ? type : "all";
    $[0] = type;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const urlContentType = t1;
  const activeCardType = isExploreContentType(type) ? EXPLORE_TYPE_TO_CARD_TYPE[type] : null;
  const [pendingTag, setPendingTag] = useState(null);
  const [pendingContentType, setPendingContentType] = useState(null);
  const activeTag = pendingTag ?? urlTag;
  const activeContentType = pendingContentType ?? urlContentType;
  const isTagLoading = pendingTag !== null || pendingContentType !== null;
  const isMobile = useMediaQuery("(max-width: 767px)");
  const filterModalTopOffset = isMobile ? logoHeight : undefined;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setPendingTag(null);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== urlTag) {
    t3 = [urlTag];
    $[3] = urlTag;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setPendingContentType(null);
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== urlContentType) {
    t5 = [urlContentType];
    $[6] = urlContentType;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] !== t) {
    t6 = t("worlds-landing.explore-viverse.content-type.all");
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = {
      key: "all",
      label: t6
    };
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t) {
    t8 = t("worlds-landing.explore-viverse.content-type.game");
    $[12] = t;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = {
      key: "game",
      label: t8,
      icon: IconGamepad
    };
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t) {
    t10 = t("worlds-landing.explore-viverse.content-type.experience");
    $[16] = t;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10) {
    t11 = {
      key: "experience",
      label: t10,
      icon: IconExperience
    };
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11 || $[21] !== t7 || $[22] !== t9) {
    t12 = [t7, t9, t11];
    $[20] = t11;
    $[21] = t7;
    $[22] = t9;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const contentTypeTags = t12;
  let t13;
  if ($[24] !== promotedRoomTags) {
    t13 = [...promotedRoomTags.map(_temp)];
    $[24] = promotedRoomTags;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const filterTags = t13;
  let t14;
  if ($[26] !== activeTag || $[27] !== hash || $[28] !== isTagLoading || $[29] !== pathname || $[30] !== router || $[31] !== searchParams) {
    t14 = tag_0 => {
      if (isTagLoading) {
        return;
      }
      const params = new URLSearchParams(searchParams.toString());
      if (tag_0 !== "all-experiences" && tag_0 !== activeTag) {
        params.set(SEARCH_PARAM_KEYS.GENRE, tag_0);
      } else {
        params.delete(SEARCH_PARAM_KEYS.GENRE);
      }
      const queryString = params.toString();
      if (queryString === searchParams.toString()) {
        setPendingTag(null);
        return;
      }
      setPendingTag(tag_0 as TagName);
      replaceUrlWithQueryString(router, pathname, queryString, hash, false);
    };
    $[26] = activeTag;
    $[27] = hash;
    $[28] = isTagLoading;
    $[29] = pathname;
    $[30] = router;
    $[31] = searchParams;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const handleTagClick = t14;
  let t15;
  if ($[33] !== activeContentType || $[34] !== hash || $[35] !== isTagLoading || $[36] !== pathname || $[37] !== router || $[38] !== searchParams) {
    t15 = nextContentType => {
      if (isTagLoading || nextContentType === activeContentType) {
        return;
      }
      const params_0 = new URLSearchParams(searchParams.toString());
      if (nextContentType === "all") {
        params_0.delete(SEARCH_PARAM_KEYS.TYPE);
      } else {
        params_0.set(SEARCH_PARAM_KEYS.TYPE, nextContentType);
      }
      const queryString_0 = params_0.toString();
      if (queryString_0 === searchParams.toString()) {
        setPendingContentType(null);
        return;
      }
      setPendingContentType(nextContentType);
      replaceUrlWithQueryString(router, pathname, queryString_0, hash, false);
    };
    $[33] = activeContentType;
    $[34] = hash;
    $[35] = isTagLoading;
    $[36] = pathname;
    $[37] = router;
    $[38] = searchParams;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  const handleContentTypeChange = t15;
  let t16;
  if ($[40] !== mobileNavHeight) {
    t16 = {
      marginBottom: mobileNavHeight
    };
    $[40] = mobileNavHeight;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t) {
    t17 = t("worlds-landing.explore-viverse.title");
    $[42] = t;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t17) {
    t18 = <h2 className="header-5 px-4 max-md:text-base md:px-6">{t17}</h2>;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== handleContentTypeChange) {
    t19 = nextContentType_0 => handleContentTypeChange(nextContentType_0 as ContentTypeTagKey);
    $[46] = handleContentTypeChange;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== activeContentType || $[49] !== activeTag || $[50] !== contentTypeTags || $[51] !== filterTags || $[52] !== handleTagClick || $[53] !== isTagLoading || $[54] !== t19) {
    t20 = <div data-component="explore-tags" className="flex flex-col gap-3 py-4 md:min-w-0 md:flex-1 md:flex-row md:flex-nowrap md:gap-2 md:px-6 lg:px-6"><ExploreViverseTags contentTypeTags={contentTypeTags} activeContentType={activeContentType} setActiveContentType={t19} filterTags={filterTags} activeTag={activeTag} disabled={isTagLoading} setActiveTag={handleTagClick} /></div>;
    $[48] = activeContentType;
    $[49] = activeTag;
    $[50] = contentTypeTags;
    $[51] = filterTags;
    $[52] = handleTagClick;
    $[53] = isTagLoading;
    $[54] = t19;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== filterModalTopOffset) {
    t21 = <SearchToolbar filterModalTopOffset={filterModalTopOffset} hideType={true} />;
    $[56] = filterModalTopOffset;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== t20 || $[59] !== t21) {
    t22 = <div className="flex flex-col md:flex-row md:items-center md:justify-between">{t20}{t21}</div>;
    $[58] = t20;
    $[59] = t21;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Separator className="bg-vive-surface-normal mt-4 lg:mt-0" />;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== t22) {
    t24 = <div className="bg-background/80 sticky top-0 z-30 flex flex-col backdrop-blur-md">{t22}{t23}</div>;
    $[62] = t22;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  let t25;
  if ($[64] !== activeCardType || $[65] !== activeTag || $[66] !== urlContentType) {
    t25 = urlContentType === "all" ? <ExploreViverseAllSections activeTag={activeTag} /> : <ExploreViverseCardGrid activeTag={activeTag} activeCardType={activeCardType} />;
    $[64] = activeCardType;
    $[65] = activeTag;
    $[66] = urlContentType;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== t16 || $[69] !== t18 || $[70] !== t24 || $[71] !== t25) {
    t26 = <section id="explore-viverse" data-section="explore-viverse" className="flex scroll-mt-5 flex-col gap-[18px]" style={t16}>{t18}{t24}{t25}</section>;
    $[68] = t16;
    $[69] = t18;
    $[70] = t24;
    $[71] = t25;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  return t26;
}
function _temp(tag) {
  return {
    key: tag.name,
    label: tag.translations?.[0]?.name ?? tag.name
  };
}
export function ExploreViverseSkeleton() {
  const $ = _c(15);
  const t = useTranslations();
  const {
    mobileNavHeight
  } = useWorldSearchContext();
  const skeletonCount = useFallbackSkeletons(EXPLORE_VIVERSE_LIMIT);
  let t0;
  if ($[0] !== t) {
    t0 = t("worlds-landing.explore-viverse.title");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <h2 className="header-5 md:header-4 px-4 md:px-6">{t0}</h2>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex flex-col gap-2"><div className="no-scrollbar flex gap-2 overflow-x-auto px-4 py-4 md:flex-wrap md:overflow-x-hidden md:px-6 lg:px-6"><ExploreViverseTagsSkeleton /></div><Separator className="bg-vive-surface-normal" /></div>;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== mobileNavHeight) {
    t3 = {
      marginBottom: mobileNavHeight
    };
    $[5] = mobileNavHeight;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== skeletonCount) {
    t4 = <div className={CARD_GRID_AUTO}>{Array.from({
        length: skeletonCount
      }).map(_temp2)}</div>;
    $[7] = skeletonCount;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <div className="px-4 md:px-6" style={t3}>{t4}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t1 || $[13] !== t5) {
    t6 = <section className="flex flex-col gap-[18px]">{t1}{t2}{t5}</section>;
    $[12] = t1;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function _temp2(_, index) {
  return <ViverseWorldCardSkeleton key={`skeleton-${index}`} className="h-auto p-0" />;
}